import {
  WebSocketMessage,
  MessageType,
  WebSocketState,
  WebSocketConfig,
  WebSocketEventHandlers,
  ChatMessageData,
  DiceRollData,
  AssetSharedData,
  SystemNotificationData,
  InitiativeEntry,
  InitiativeUpdateData,
  InitiativeAddData,
  InitiativeRemoveData,
  CombatStateChangeData,
  HealthUpdateData,
  StatusEffectData,
  SessionControlData,
} from "../types/websocket";

export class WebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig;
  private eventHandlers: WebSocketEventHandlers = {};
  private state: WebSocketState = WebSocketState.DISCONNECTED;
  private reconnectAttempts = 0;
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private pingInterval: NodeJS.Timeout | null = null;
  private messageListeners: Map<string, Set<(_data: unknown) => void>> = new Map();

  constructor(config: WebSocketConfig) {
    this.config = {
      autoReconnect: true,
      maxReconnectAttempts: 5,
      reconnectInterval: 3000,
      ...config,
    };
  }

  // Connection Management
  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Prevent multiple simultaneous connection attempts. Resolve (rather than
      // leaving the promise pending forever) so callers awaiting connect() unblock.
      if (this.state === WebSocketState.CONNECTING) {
        resolve();
        return;
      }

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      // Close existing connection if any
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }

      this.setState(WebSocketState.CONNECTING);

      // Build WebSocket URL with query parameters. Read the token from localStorage
      // (the single source of truth, updated on silent refresh) so reconnects after
      // a token rotation don't present a stale/expired JWT.
      const url = new URL(this.config.url);
      const freshToken =
        (typeof localStorage !== "undefined" && localStorage.getItem("authToken")) ||
        this.config.token ||
        "temp-token";
      url.searchParams.set("userId", this.config.userId);
      url.searchParams.set("username", this.config.username || this.config.userId);
      url.searchParams.set("token", freshToken);

      // No session ID in URL for global WebSocket connection

      this.ws = new WebSocket(url.toString());

      this.ws.onopen = () => {
        this.setState(WebSocketState.CONNECTED);
        this.reconnectAttempts = 0; // reset so future disconnects get a fresh retry budget
        this.stopPingInterval();
        this.startPingInterval();
        this.eventHandlers.onConnect?.();

        // Test: Send a diagnostic ping to verify connection
        this.sendMessage("ping", { timestamp: Date.now(), test: "connection_test" });

        // Note: Session joining is now handled separately via joinSession method
        // No automatic session joining on global WebSocket connection

        resolve();
      };

      this.ws.onclose = (event) => {
        this.setState(WebSocketState.DISCONNECTED);
        this.stopPingInterval();
        this.eventHandlers.onDisconnect?.(event.code, event.reason);

        // Only auto-reconnect for certain close codes and if explicitly enabled
        const shouldReconnect =
          this.config.autoReconnect &&
          this.reconnectAttempts < (this.config.maxReconnectAttempts || 5) &&
          (event.code === 1001 || // Going away (page refresh/navigation)
            event.code === 1006 || // Abnormal closure (network issues)
            event.code === 1011 || // Server error
            event.code === 1012); // Service restart

        if (shouldReconnect) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error("❌ WebSocket error occurred:", error);
        console.error("🔍 Error details:", {
          type: error.type,
          target: error.target,
          currentTarget: error.currentTarget,
          readyState: this.ws?.readyState,
          url: this.ws?.url,
        });
        this.setState(WebSocketState.ERROR);
        this.eventHandlers.onError?.(error);
        reject(error);
      };

      this.ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error("❌ Failed to parse WebSocket message:", error);
          console.error("❌ Raw message data:", event.data);
        }
      };
    });
  }

  public disconnect(): void {
    // Disable auto-reconnect before disconnecting
    this.config.autoReconnect = false;
    this.clearReconnectTimeout();
    this.stopPingInterval();

    if (this.ws) {
      // Only close if not already closed/closing
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close(1000, "User disconnect");
      }
      this.ws = null;
    }

    this.setState(WebSocketState.DISCONNECTED);
  }

  public isConnected(): boolean {
    return this.state === WebSocketState.CONNECTED && this.ws?.readyState === WebSocket.OPEN;
  }

  public getState(): WebSocketState {
    return this.state;
  }

  // Event Handlers
  public setEventHandlers(handlers: WebSocketEventHandlers): void {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };
  }

  // Per-message-type event listeners
  public on<T = unknown>(type: MessageType, handler: (_data: T) => void): void {
    if (!this.messageListeners.has(type)) {
      this.messageListeners.set(type, new Set());
    }
    this.messageListeners.get(type)!.add(handler as (_data: unknown) => void);
  }

  public off<T = unknown>(type: MessageType, handler: (_data: T) => void): void {
    this.messageListeners.get(type)?.delete(handler as (_data: unknown) => void);
  }

  // Message Sending
  public sendMessage(
    type: MessageType,
    data: unknown,
    recipients?: string[],
    sessionId?: string
  ): boolean {
    if (!this.isConnected()) {
      console.warn("⚠️ Cannot send message: WebSocket not connected");
      return false;
    }

    const message: WebSocketMessage = {
      id: crypto.randomUUID(),
      timestamp: Date.now().toString(),
      type,
      sessionId: sessionId || "",
      senderId: this.config.userId + "",
      data,
      recipients,
    };

    try {
      this.ws!.send(JSON.stringify(message));
      return true;
    } catch (error) {
      console.error("❌ Failed to send message:", error);
      return false;
    }
  }

  // Convenience methods for common message types
  public sendChatMessage(
    message: string,
    messageType: string = "IC",
    characterName?: string,
    recipientId?: string,
    sessionId?: string
  ): boolean {
    const data: ChatMessageData = {
      message,
      messageType,
      characterName,
      recipientId,
    };

    const type: MessageType =
      messageType === "whisper"
        ? "chat_whisper"
        : messageType === "OOC"
          ? "chat_ooc"
          : messageType === "IC"
            ? "chat_ic"
            : "chat_message";

    return this.sendMessage(type, data, recipientId ? [recipientId] : undefined, sessionId);
  }

  public sendDiceRoll(
    formula: string,
    rollName?: string,
    isPrivate: boolean = false,
    characterId?: string,
    sessionId?: string,
    characterName?: string
  ): boolean {
    // Reject formulas with no dice term so we never broadcast a bogus roll.
    if (!/\d*d\d+/i.test(formula)) {
      console.warn("⚠️ Invalid dice formula, not rolling:", formula);
      return false;
    }

    const diceRoll = this.rollDice(formula);

    const data: DiceRollData = {
      rollId: this.generateId(),
      formula,
      results: diceRoll.results,
      total: diceRoll.total,
      rollName,
      characterId,
      characterName,
      isPrivate,
      isCritical: diceRoll.isCritical,
    };

    return this.sendMessage("dice_roll", data, undefined, sessionId);
  }

  public shareAsset(
    assetId: string,
    assetName: string,
    assetType: string,
    permissions: string[] = [],
    sessionId?: string
  ): boolean {
    const data: AssetSharedData = {
      assetId,
      assetName,
      assetType,
      sharedBy: this.config.userId,
      permissions,
    };

    return this.sendMessage("asset_shared", data, undefined, sessionId);
  }

  public sendSystemNotification(
    message: string,
    notificationType: string = "info",
    severity: "info" | "warning" | "error" = "info",
    sessionId?: string
  ): boolean {
    const data: SystemNotificationData = {
      notificationType,
      message,
      userId: this.config.userId,
      severity,
    };

    return this.sendMessage("system_alert", data, undefined, sessionId);
  }

  public sendPing(): boolean {
    return this.sendMessage("ping", { timestamp: Date.now() });
  }

  // Session Management
  public joinSession(sessionId: string, characterId?: string, characterName?: string): boolean {
    return this.sendMessage(
      "connect",
      {
        sessionId,
        userId: this.config.userId,
        username: this.config.username,
        characterId,
        characterName,
      },
      undefined,
      sessionId
    );
  }

  public leaveSession(sessionId: string): boolean {
    return this.sendMessage(
      "disconnect",
      {
        sessionId,
        userId: this.config.userId,
      },
      undefined,
      sessionId
    );
  }

  // Initiative and Combat Management
  public sendInitiativeUpdate(
    sessionId: string,
    entries: InitiativeEntry[],
    currentTurn: number,
    round: number,
    isTracking: boolean
  ): boolean {
    const data: InitiativeUpdateData = {
      entries,
      currentTurn,
      round,
      isTracking,
    };

    return this.sendMessage("initiative_update", data, undefined, sessionId);
  }

  public addToInitiative(sessionId: string, entry: InitiativeEntry): boolean {
    const data: InitiativeAddData = {
      entry,
    };

    return this.sendMessage("initiative_add", data, undefined, sessionId);
  }

  public removeFromInitiative(sessionId: string, entryId: string): boolean {
    const data: InitiativeRemoveData = {
      entryId,
    };

    return this.sendMessage("initiative_remove", data, undefined, sessionId);
  }

  public changeTurn(sessionId: string, direction: "next" | "prev"): boolean {
    return this.sendMessage("turn_change", { direction }, undefined, sessionId);
  }

  public updateCombatState(sessionId: string, state: string): boolean {
    const data: CombatStateChangeData = {
      state,
    };

    return this.sendMessage("combat_state_change", data, undefined, sessionId);
  }

  public updateHealth(
    sessionId: string,
    entryId: string,
    hp: number,
    maxHp: number,
    change: number,
    changeType: "damage" | "healing"
  ): boolean {
    const data: HealthUpdateData = {
      entryId,
      hp,
      maxHp,
      change,
      changeType,
    };

    return this.sendMessage("health_update", data, undefined, sessionId);
  }

  public updateStatusEffects(
    sessionId: string,
    entryId: string,
    conditions: string[],
    added?: string[],
    removed?: string[]
  ): boolean {
    const data: StatusEffectData = {
      entryId,
      conditions,
      added,
      removed,
    };

    return this.sendMessage("status_effect", data, undefined, sessionId);
  }

  public sendSessionControl(sessionId: string, action: string): boolean {
    const data: SessionControlData = {
      action,
    };

    return this.sendMessage("session_control", data, undefined, sessionId);
  }

  /**
   * DM broadcasts a music control event (play / pause / stop / volume / seek) to all
   * session participants.  Players that receive this message should react to it in
   * their local audio player.
   */
  public sendMusicControl(
    sessionId: string,
    action: "play" | "pause" | "stop" | "volume" | "seek",
    trackUrl?: string,
    trackTitle?: string,
    position?: number,
    volume?: number
  ): boolean {
    return this.sendMessage(
      "music_control",
      { action, trackUrl, trackTitle, position, volume },
      undefined,
      sessionId
    );
  }

  // Private Methods
  private setState(newState: WebSocketState): void {
    if (this.state !== newState) {
      this.state = newState;
      this.eventHandlers.onStateChange?.(newState);
    }
  }

  private handleMessage(message: WebSocketMessage): void {
    // Handle special message types
    switch (message.type) {
      case "ping":
        // Respond to ping with pong
        this.sendMessage("pong", { timestamp: Date.now() });
        break;

      case "pong":
        // Handle pong response
        break;

      case "error":
        console.error("❌ Server error:", message.data);
        // Forward to the app handler so the error surfaces in the UI, not just the console.
        this.eventHandlers.onMessage?.(message);
        break;

      default:
        // Pass message to the registered handler
        if (this.eventHandlers.onMessage) {
          this.eventHandlers.onMessage(message);
        } else {
          console.warn("⚠️ No event handler registered for message:", message.type);
        }
        break;
    }

    // Dispatch to per-type listeners regardless of type
    const listeners = this.messageListeners.get(message.type);
    if (listeners) {
      listeners.forEach((handler) => handler(message.data));
    }
  }

  private scheduleReconnect(): void {
    // Don't reconnect if we're already trying to reconnect
    if (this.reconnectTimeout) {
      return;
    }

    this.reconnectAttempts++;

    // Cap the delay at 30 seconds maximum
    const baseDelay = this.config.reconnectInterval || 3000;
    const exponentialDelay = baseDelay * Math.pow(2, this.reconnectAttempts - 1);
    const delay = Math.min(exponentialDelay, 30000);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null; // Clear the timeout reference

      // Check if we should still reconnect
      if (
        this.config.autoReconnect &&
        this.reconnectAttempts <= (this.config.maxReconnectAttempts || 5)
      ) {
        this.connect().catch((error) => {
          console.error("❌ Reconnect failed:", error);
          // If connection fails, the onclose handler will schedule another reconnect
        });
      } else {
        this.setState(WebSocketState.ERROR);
      }
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
  }

  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.isConnected()) {
        this.sendPing();
      }
    }, 30000); // Ping every 30 seconds
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private rollDice(formula: string): { results: number[]; total: number; isCritical: boolean } {
    // Keep-highest/lowest with optional +/-modifier: e.g. 2d20kh1+5 (advantage), 2d20kl1-1
    const keepHighestMatch = formula.match(/^(\d*)d(\d+)kh(\d+)(?:([+-])(\d+))?/i);
    const keepLowestMatch = formula.match(/^(\d*)d(\d+)kl(\d+)(?:([+-])(\d+))?/i);

    let numDice: number;
    let sides: number;
    let keepCount: number | null = null;
    let keepHighest = true;
    let modifier = 0;

    // An omitted dice count (e.g. "d20") means 1.
    const parseCount = (s: string) => (s === "" ? 1 : parseInt(s));
    const parseMod = (sign?: string, val?: string) =>
      sign && val ? (sign === "+" ? parseInt(val) : -parseInt(val)) : 0;

    if (keepHighestMatch) {
      numDice = parseCount(keepHighestMatch[1]);
      sides = parseInt(keepHighestMatch[2]);
      keepCount = parseInt(keepHighestMatch[3]);
      keepHighest = true;
      modifier = parseMod(keepHighestMatch[4], keepHighestMatch[5]);
    } else if (keepLowestMatch) {
      numDice = parseCount(keepLowestMatch[1]);
      sides = parseInt(keepLowestMatch[2]);
      keepCount = parseInt(keepLowestMatch[3]);
      keepHighest = false;
      modifier = parseMod(keepLowestMatch[4], keepLowestMatch[5]);
    } else {
      // Basic NdS+/-M format (ignore non-numeric modifiers like +dex)
      const match = formula.match(/(\d*)d(\d+)(?:([+-])(\d+))?/i);
      if (!match) {
        return { results: [], total: 0, isCritical: false };
      }
      numDice = parseCount(match[1]);
      sides = parseInt(match[2]);
      modifier = parseMod(match[3], match[4]);
    }

    const results: number[] = [];
    for (let i = 0; i < numDice; i++) {
      results.push(Math.floor(Math.random() * sides) + 1);
    }

    let keptResults = results;
    if (keepCount !== null) {
      const sorted = [...results].sort((a, b) => (keepHighest ? b - a : a - b));
      keptResults = sorted.slice(0, keepCount);
    }

    const total = keptResults.reduce((sum, r) => sum + r, 0) + modifier;

    // Critical: a *kept* d20 die showing 20 (disadvantage that discards the 20 is not a crit)
    const isCritical = sides === 20 && keptResults.some((r) => r === 20);

    return { results, total, isCritical };
  }

  // Update session ID (legacy method - now just logs a warning)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public updateSessionId(_sessionId: string): void {
    console.warn("⚠️ updateSessionId is deprecated. Use joinSession/leaveSession instead.");
  }

  // Get connection stats
  public getConnectionInfo(): {
    state: WebSocketState;
    reconnectAttempts: number;
    sessionId?: string;
  } {
    return {
      state: this.state,
      reconnectAttempts: this.reconnectAttempts,
      sessionId: undefined, // No longer stored in config
    };
  }
}
